import MediaThumbnail from "../../../components/common/MediaThumbnail";
import React, { useEffect, useRef, useState } from "react";
import {
  CalendarDays,
  Eye,
  Images,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { normalizeMedia } from "../projectMediaApi";
import ImageViewer from "../../../components/common/ImageViewer";
import "./ProjectUpdates.css";

const updateDate = (update) =>
  update.updateDate ||
  update.update_date ||
  update.createdAt ||
  update.created_at;
function timestamp(update) {
  const value = Date.parse(update.createdAt || update.created_at);
  return Number.isNaN(value) ? null : value;
}
function formattedDate(update) {
  const value = Date.parse(updateDate(update));
  return Number.isNaN(value)
    ? "Date not available"
    : new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(value);
}
function Metadata({ update, expanded = false }) {
  const author =
    update.createByName || update.createdByName || update.created_by_name;
  return (
    <div className="project-update-meta">
      {update.projectName && (
        <>
          <span>{update.projectName}</span>
          <span aria-hidden="true">&bull;</span>
        </>
      )}
      <CalendarDays size={12} />
      <time>{formattedDate(update)}</time>
      {author && (
        <>
          <span aria-hidden="true">{expanded ? "|" : "?"}</span>
          {expanded && <UserRound size={16} />}
          <span>{author}</span>
        </>
      )}
    </div>
  );
}
function UpdateOptions({ title }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const trigger = useRef(null);
  useEffect(() => {
    if (!open) return;
    const dismiss = (event) => {
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("focusin", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("focusin", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  return (
    <div className="project-update-options" ref={ref}>
      <button
        type="button"
        ref={trigger}
        className="project-update-options-trigger"
        aria-label={"More options for " + title}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <MoreVertical size={22} />
      </button>
      {open && (
        <div className="project-update-options-popover">
          <button
            type="button"
            className="project-update-delete"
            disabled
            aria-label={"Delete " + title}
            title="Deleting updates is not available yet"
          >
            <Trash2 size={18} />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
export function UpdateView({ update, onClose }) {
  const ref = useRef(null);
  const [photoIndex, setPhotoIndex] = useState(null);
  const photos = normalizeMedia({ images: update.images });
  useEffect(() => {
    const previous = document.activeElement;
    const dialog = ref.current;
    dialog.showModal();
    return () => {
      dialog.close();
      previous?.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className="project-action-dialog project-update-view"
      aria-labelledby="update-view-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <header>
        <h2 id="update-view-title">{update.title || "Project update"}</h2>
        <button type="button" aria-label="Close update" onClick={onClose}>
          <X size={20} />
        </button>
      </header>
      <div className="project-update-view-body">
        <Metadata update={update} />
        <p className="project-update-description">
          {update.description || update.message || "No description provided."}
        </p>
        <h3>Photos ({photos.length})</h3>
        {photos.length ? (
          <div className="project-update-view-photos">
            {photos.map((photo, index) => (
              <button
                type="button"
                className="image-preview-button"
                key={photo.key}
                onClick={() => setPhotoIndex(index)}
                aria-label={"View photo " + (index + 1)}
              >
                <MediaThumbnail
                  image={photo}
                  alt={
                    (update.title || "Project update") + " photo " + (index + 1)
                  }
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        ) : (
          <p className="detail-empty">No photos attached to this update.</p>
        )}
      </div>
      <footer>
        <button className="detail-button" onClick={onClose}>
          Close
        </button>
      </footer>
      {photoIndex !== null && (
        <ImageViewer
          images={photos}
          initialIndex={photoIndex}
          onClose={() => setPhotoIndex(null)}
        />
      )}
    </dialog>
  );
}
export default function ProjectUpdates({
  projectId,
  updates,
  loading,
  error,
  onRetry,
  readOnly = false,
  showInlineImages = false,
  sortOrder,
  onSortChange,
  totalCount,
}) {
  const [order, setOrder] = useState("newest");
  const [viewed, setViewed] = useState(null);
  const [gallery, setGallery] = useState(null);
  const sorted = updates.slice().sort((a, b) => {
    const first = timestamp(a),
      second = timestamp(b);
    if (first === null) return second === null ? 0 : 1;
    if (second === null) return -1;
    return (sortOrder || order) === "newest" ? second - first : first - second;
  });
  return (
    <>
      {/* <header className="project-updates-heading"><div><h2>Project Updates</h2><p>Track construction progress and share important site milestones.</p></div>{!readOnly && <Link className="detail-button detail-primary" to={"/admin/projects/" + projectId + "/updates/new"}><Plus size={14} />Add Update</Link>}</header> */}
      <div className="project-updates-body">
        {loading ? (
          <p className="detail-empty">Fetching project updates...</p>
        ) : error ? (
          <div role="alert">
            <p>{error}</p>
            <button className="detail-button" onClick={onRetry}>
              Retry updates
            </button>
          </div>
        ) : (
          <>
            <div className="project-updates-toolbar">
              <span>
                {totalCount ?? updates.length} progress{" "}
                {(totalCount ?? updates.length) === 1 ? "update" : "updates"}
              </span>
              <select
                aria-label="Sort updates"
                value={sortOrder || order}
                onChange={(event) => {
                  setOrder(event.target.value);
                  onSortChange?.(event.target.value);
                }}
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>
            </div>
            {sorted.length ? (
              <ol className="project-updates-timeline">
                {sorted.map((update, index) => {
                  const photos = normalizeMedia({ images: update.images });
                  const actions = (
                    <div className="project-update-actions">
                      <button
                        className="detail-button project-update-view-button"
                        onClick={() => setViewed(update)}
                        aria-label={
                          "View " + (update.title || "project update")
                        }
                      >
                        <Eye size={16} />View
                      </button>
                      {!readOnly && (
                        <>
                          <button
                            className="detail-button"
                            disabled
                            title="Editing updates is not available yet"
                          >
                            <Pencil size={16} />
                            Edit
                          </button>
                        </>
                      )}
                    </div>
                  );
                  return (
                    <li key={update.id ?? index}>
                      <article
                        className="project-update-card compact-update-card"
                      >
                        <div className="project-update-card-top">
                          <div>
                            <div className="project-update-title">
                              <h3>{update.title || "Project update"}</h3>
                              {photos.length > 0 && (
                                <span className="project-update-photo-count">
                                  <Images size={12} />
                                  {photos.length}{" "}
                                  {photos.length === 1 ? "photo" : "photos"}
                                </span>
                              )}
                            </div>
                            <Metadata update={update} expanded />
                          </div>
                          {!readOnly && (
                            <UpdateOptions
                              title={update.title || "project update"}
                            />
                          )}
                        </div>
                        <p className="project-update-description project-update-summary">
                          {update.description ||
                            update.message ||
                            "No description provided."}
                        </p>
                        {(!readOnly || showInlineImages) &&
                          photos.length > 0 && (
                            <div
                              className="project-update-inline-photos"
                            >
                              {photos.slice(0, 3).map((photo, photoIndex) => (
                                <button
                                  type="button"
                                  className="image-preview-button"
                                  key={photo.key}
                                  aria-label={
                                    "View photo " +
                                    (photoIndex + 1) +
                                    " of " +
                                    (update.title || "project update")
                                  }
                                  onClick={() =>
                                    setGallery({ photos, index: photoIndex })
                                  }
                                >
                                  <MediaThumbnail
                                    image={photo}
                                    alt={
                                      (update.title || "Project update") +
                                      " photo " +
                                      (photoIndex + 1)
                                    }
                                    loading="lazy"
                                  />
                                </button>
                              ))}
                              {photos.length > 3 && (
                                <button
                                  type="button"
                                  className="project-update-more-photos"
                                  aria-label={
                                    "View " +
                                    (photos.length - 3) +
                                    " more photos of " +
                                    (update.title || "project update")
                                  }
                                  onClick={() =>
                                    setGallery({ photos, index: 3 })
                                  }
                                >
                                  <Images size={26} aria-hidden="true" />
                                  <strong>+{photos.length - 3}</strong>
                                  <span>more photos</span>
                                </button>
                              )}
                            </div>
                          )}
                        <footer className="project-update-card-footer">
                            {actions}
                          </footer>
                      </article>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="detail-empty">No project updates to display.</p>
            )}
          </>
        )}
      </div>
      {gallery && (
        <ImageViewer
          images={gallery.photos}
          initialIndex={gallery.index}
          onClose={() => setGallery(null)}
        />
      )}
      {viewed && <UpdateView update={viewed} onClose={() => setViewed(null)} />}
    </>
  );
}
